<template>
  <div class="form-group" :class="{ 'has-icon': hasIcon, disabled }">
    <label :for="id" v-if="label">{{ label }}</label>

    <div class="position-relative">
      <div class="slot-icon-wrapper">
        <slot name="icon"></slot>
      </div>

      <el-select
        class="d-block"
        v-if="elSelect"
        v-model="innerValue"
        filterable
        :class="classObject"
        :clearable="nullable"
        :placeholder="placeholder"
        :disabled="disabled"
      >
        <el-option
          v-for="option in options"
          :key="option.id"
          :label="optionLabelFunction(option)"
          :value="option.id"
        >
        </el-option>
      </el-select>
      <select
        v-else-if="select"
        class="form-control"
        :class="classObject"
        :id="id"
        v-model="innerValue"
        :disabled="disabled"
        :required="required"
      >
        <option value="" :disabled="!nullable">{{ placeholder }}</option>
        <option
          v-for="(option, index) in options"
          :key="index"
          :value="option.id"
        >
          {{ optionLabelFunction(option) }}
        </option>
      </select>
      <the-mask
        v-else-if="mask.length"
        :mask="mask"
        :masked="masked"
        v-model="innerValue"
        class="form-control"
        :class="classObject"
        :id="id"
        :type="type"
        :maxLength="maxLength"
        :placeholder="placeholder"
        :autocomplete="disableAutocomplete ? 'off' : ''"
        :autocorrect="disableAutocapitalize ? 'off' : ''"
        :autocapitalize="disableAutocapitalize ? 'none' : ''"
        :readonly="readonly"
        :disabled="disabled"
        :required="required"
      />
      <textarea
        v-else-if="textarea"
        v-model="innerValue"
        class="form-control"
        :class="classObject"
        :id="id"
        :placeholder="placeholder"
        :readonly="readonly"
        :disabled="disabled"
        :required="required"
      />
      <input
        v-else
        v-model="innerValue"
        class="form-control"
        :class="classObject"
        :id="id"
        :name="name"
        :type="type"
        :placeholder="placeholder"
        :autocomplete="disableAutocomplete ? 'off' : ''"
        :autocorrect="disableAutocapitalize ? 'off' : ''"
        :autocapitalize="disableAutocapitalize ? 'none' : ''"
        :readonly="readonly"
        :disabled="disabled"
        :required="required"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
      />
    </div>

    <button v-if="withClearButton && value.length" class="clear" @click="clear">
      <i class="icon-x"></i>
    </button>
    <div v-if="showError" class="invalid-feedback">{{ errors[0] }}</div>
    <small class="tip" v-if="tip">{{ tip }}</small>
  </div>
</template>

<script>
import { Select, Option } from "element-ui";

export default {
  components: {
    "el-select": Select,
    "el-option": Option
  },
  props: {
    name: String,
    maxLength: [String, Number],
    mask: {
      type: [String, Array],
      default: ""
    },
    masked: {
      type: Boolean,
      default: false
    },
    textarea: {
      type: Boolean,
      default: false
    },
    tip: {
      type: String,
      default: ""
    },
    label: {
      type: String,
      default: ""
    },
    hasIcon: Boolean,
    type: {
      type: String,
      default: "text"
    },
    placeholder: {
      type: String,
      default: ""
    },
    id: {
      type: String,
      required: true
    },
    required: {
      type: Boolean,
      default: false
    },
    value: [String, Number],
    customClass: {
      type: String,
      default: ""
    },
    errors: {
      type: Array,
      default: () => []
    },
    disableAutocapitalize: {
      type: Boolean,
      default: false
    },
    disableAutocomplete: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    withClearButton: {
      type: Boolean,
      default: false
    },
    select: {
      type: Boolean,
      default: false
    },
    elSelect: {
      type: Boolean,
      default: false
    },
    nullable: {
      type: Boolean,
      default: false
    },
    optionLabelFunction: {
      type: Function,
      default: item => item.name
    },
    options: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      innerValue: this.value,
      showError: false
    };
  },
  computed: {
    classObject() {
      const result = { "is-invalid": this.showError };
      result[this.customClass] = true;
      return result;
    }
  },
  watch: {
    errors(newVal) {
      if (newVal && newVal.length) {
        this.showError = true;
      }
    },
    innerValue(newVal) {
      if (newVal != this.value) {
        this.showError = false;
        this.$emit("input", newVal);
      }
    },
    value(newVal) {
      if (this.innerValue !== newVal) {
        this.innerValue = newVal;
      }
    }
  },
  methods: {
    clear() {
      this.$emit("input", "");
    }
  }
};
</script>

<style lang="scss" scoped>
.form-group {
  position: relative;

  textarea.form-control {
    padding: 16px;
  }

  &.has-icon {
    .slot-icon-wrapper {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
    }

    .form-control {
      padding-left: 48px;
    }
  }

  .clear {
    position: absolute;
    height: 38px;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    background: transparent;
    border: none;
  }
}
</style>
